/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, inject, OnInit } from '@angular/core';
import { PrintService } from '../print.service';
import { NEVER, Observable } from 'rxjs';
import { PdfDialogComponent } from '../../../../../core/ui/pdf/pdf-dialog.component';
import { ErrorStateMatcher } from '@angular/material/core';
import {
    MatDialog,
    MatDialogConfig,
    MatDialogRef,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
} from '@angular/material/dialog';
import { LoggerService } from '../../../../shared/services/logger.service';
import { PrintOptionsState } from '../state/print-options.state';
import { Store } from '@ngxs/store';
import { MapPrintOptions, PaperSize, PdfPrintResult } from '../state/print-options.model';
import { concatMap, first, tap } from 'rxjs/operators';
import { SetPrintOptions } from '../state/print-options.actions';
import { FormGroupDirective, NgForm, UntypedFormControl, FormsModule } from '@angular/forms';
import { DeviceService } from '../../../../shared/services/device.service';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { LayoutDirective } from '@ngbracket/ngx-layout/flex';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatProgressBar } from '@angular/material/progress-bar';
import { NgClass, AsyncPipe } from '@angular/common';
import { ClassDirective } from '@ngbracket/ngx-layout';
import { MatButton } from '@angular/material/button';

export class PrintErrorMatcher implements ErrorStateMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const isSubmitted = form && form.submitted;
        return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }
}

@Component({
    selector: 'dash-gis-print-dialog',
    templateUrl: './print-dialog.component.html',
    styleUrls: ['./print-dialog.component.scss'],
    providers: [PrintService],
    imports: [
        MatDialogTitle,
        FormsModule,
        CdkTrapFocus,
        CdkScrollable,
        MatDialogContent,
        LayoutDirective,
        MatCheckbox,
        MatFormField,
        MatInput,
        MatError,
        MatSelect,
        MatOption,
        MatProgressBar,
        NgClass,
        ClassDirective,
        MatDialogActions,
        MatButton,
        AsyncPipe,
    ],
})
export class PrintDialogComponent implements OnInit {
    paperSize$: Observable<PaperSize> = inject(Store).select(PrintOptionsState.paperSize);
    showLegend$: Observable<boolean> = inject(Store).select(PrintOptionsState.showLegend);
    showTitle$: Observable<boolean> = inject(Store).select(PrintOptionsState.showTitle);
    titleText$: Observable<string> = inject(Store).select(PrintOptionsState.titleText);
    showNorth$: Observable<boolean> = inject(Store).select(PrintOptionsState.showNorth);
    printing = false;

    private printSettings$: Observable<MapPrintOptions> = inject(Store).select(PrintOptionsState.options);

    constructor(
        private logger: LoggerService,
        private printService: PrintService,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<PrintDialogComponent>,
        private store: Store
    ) {}

    ngOnInit() {
        this.paperSize$
            .pipe(
                tap((paperSize: PaperSize) => {
                    if (!paperSize || !paperSize.value || !paperSize.value.name) {
                        return this.setPaperSize(this.paperOptions[0]);
                    } else {
                        return NEVER;
                    }
                }),
                first()
            )
            .subscribe();
    }

    public print() {
        this.printing = true;
        this.printSettings$
            .pipe(
                concatMap((settings: MapPrintOptions) => {
                    const opts: MapPrintOptions = {
                        highQuality: settings.highQuality,
                        paperSize: settings.paperSize,
                        showLegend: settings.showLegend,
                        showNorth: settings.showNorth,
                        showTitle: settings.showTitle,
                        titleText: settings.titleText,
                    };
                    return this.printService.pdfPrint(opts);
                })
            )
            .subscribe((result: PdfPrintResult) => {
                this.openPdfDialog(result);
                this.printing = false;
                this.dialogRef.close();
            });
    }

    public cancel() {
        this.printService.cancelPrint();
        this.dialogRef.close();
    }

    public paperCompareFn(p1: PaperSize, p2: PaperSize) {
        return p1 && p2 ? p1.value.name === p2.value.name : p1 === p2;
    }

    public setShowTitle(value) {
        this.store.dispatch(new SetPrintOptions({ showTitle: value }));
    }

    public setTitleText(value) {
        this.store.dispatch(new SetPrintOptions({ titleText: value }));
    }

    public setShowLegend(value) {
        this.store.dispatch(new SetPrintOptions({ showLegend: value }));
    }

    public setShowNorth(value) {
        this.store.dispatch(new SetPrintOptions({ showNorth: value }));
    }

    public setPaperSize(value) {
        this.store.dispatch(new SetPrintOptions({ paperSize: value }));
    }

    private openPdfDialog(result: PdfPrintResult) {
        const pdfConfig: MatDialogConfig = {
            data: result,
        };

        // if (!this.deviceService.isIOS) {
        pdfConfig.width = '80vw';
        pdfConfig.height = '80vh';
        pdfConfig.panelClass = 'print-pdf-container';
        // }
        this.dialog.open(PdfDialogComponent, pdfConfig);
    }

    // region Properties
    get progress$() {
        return this.printService.progress$;
    }

    get paperOptions() {
        return this.printService.paperOptions;
    }

    // endregion
}
